import { useCallback, useEffect, useRef, useState } from 'react';
import {
  searchMoleculesByText,
  type ReactionCategory,
  type StructuralConstraints,
  type TextSearchResults
} from '@peruselab/api-client/moleculeSearch';
import type { SearchRequestState } from './searchRequestState';
import { useActiveTenant } from '../tenancy/activeTenant';

// Shorter than useMoleculeSearch's 500ms (#463): Google-style live suggestions read as sluggish
// at that interval, and unlike structure search (which re-fires on every canvas edit regardless
// of user intent), a keystroke is a much more deliberate "the user wants a fresh answer" signal.
const DEBOUNCE_MS = 200;

// Mirrors useMoleculeSearch.ts's exact requestId-guard shape (#463's acceptance criteria calls
// this out explicitly: no stale/out-of-order results on rapid typing). A blank/whitespace query
// short-circuits to idle client-side purely to avoid a wasted request -- the backend classifier
// itself doesn't reject a blank query, it just returns no results for one. `mode` accepts the
// full SearchMode (including 'drawing') so callers don't need to narrow it themselves -- this
// hook simply never searches in 'drawing' mode.
export function useTextSearch(
  query: string,
  mode: 'drawing' | 'text' | 'textDrawing',
  reactionBuckets: ReadonlySet<ReactionCategory>,
  structuralConstraints: StructuralConstraints
) {
  const [search, setSearch] = useState<SearchRequestState<TextSearchResults>>({ state: 'idle' });
  const searchRequestRef = useRef(0);
  const trimmedQuery = query.trim();

  const currentResults = search.state === 'ready' && search.sourceKey === trimmedQuery ? search.results : null;
  // #465: only ever surfaced by the caller in pure 'text' mode (see WorkspaceShell) -- kept as a
  // plain derived value here rather than gated on `mode` in this hook, matching how `suggestions`
  // is already gated one level up, not duplicated in every hook that feeds it.
  const pubchemFallback = currentResults?.pubchemFallback ?? null;
  const searchIsStale =
    search.state === 'ready'
      ? search.sourceKey !== trimmedQuery
      : search.state === 'error' && search.sourceKey !== trimmedQuery;

  const { scopeKey } = useActiveTenant();
  const runSearch = useCallback(async () => {
    if (trimmedQuery === '') {
      setSearch({ state: 'idle' });
      return;
    }
    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;
    setSearch({ state: 'loading', sourceKey: trimmedQuery });
    try {
      const results = await searchMoleculesByText(trimmedQuery, reactionBuckets, structuralConstraints);
      if (searchRequestRef.current === requestId) {
        setSearch({ state: 'ready', sourceKey: trimmedQuery, results });
      }
    } catch (error) {
      if (searchRequestRef.current === requestId) {
        setSearch({
          state: 'error',
          sourceKey: trimmedQuery,
          message: error instanceof Error ? error.message : 'Molecule search failed.'
        });
      }
    }
    // #1051: re-asked when the active tenant changes -- what it can see is part of the answer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedQuery, reactionBuckets, structuralConstraints, scopeKey]);

  useEffect(() => {
    if (mode !== 'text' && mode !== 'textDrawing') return;
    if (trimmedQuery === '') {
      searchRequestRef.current += 1;
      setSearch({ state: 'idle' });
      return;
    }
    const timeout = globalThis.setTimeout(() => void runSearch(), DEBOUNCE_MS);
    // Same cancel-on-superseded-change cleanup useMoleculeSearch.ts relies on -- a keystroke
    // before the timer fires clears it before it ever runs; a response already in flight when a
    // newer keystroke lands is separately guarded by the requestId check in runSearch above.
    return () => globalThis.clearTimeout(timeout);
  }, [trimmedQuery, mode, runSearch]);

  return { search, currentResults, pubchemFallback, searchIsStale, runSearch };
}
